import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { LogOut, Calendar, Award, Clock, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const TIME_SLOTS = {
  'Lundi': ['09:00', '10:30', '12:00', '16:30', '17:30', '18:30', '19:30'],
  'Mardi': ['09:00', '10:30', '12:00', '16:30', '17:30', '18:30', '19:30'],
  'Mercredi': ['09:00', '10:30', '12:00', '16:30', '17:30', '18:30', '19:30'],
  'Jeudi': ['09:00', '10:30', '12:00', '16:30', '17:30', '18:30', '19:30'],
  'Vendredi': ['09:00', '10:30', '12:00', '16:30', '17:30', '18:30', '19:30'], // Après-midi maintenant ouvert
  'Samedi': ['09:00', '10:30', '12:00'], // Matin seulement
  'Dimanche': ['09:00', '10:30', '12:00'] // Matin seulement - Nouveau !
};

// Plus de fermeture permanente - tous les jours suivent leurs horaires normaux

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [weeklyPlanningBookings, setWeeklyPlanningBookings] = useState([]);  // NEW: For weekly planning (replaces weeklySchedule)
  const [slotsAvailability, setSlotsAvailability] = useState({}); // Track available spots per slot
  const [stats, setStats] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('booking'); // 'booking', 'history', or 'schedule'

  // Check if week is closed (only open Saturday 11h - Sunday 20h)
  const isWeekClosed = () => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
    const hour = now.getHours();
    
    // Reservations are ONLY open from Saturday 11h to Sunday 20h
    if (dayOfWeek === 6 && hour >= 11) {
      return false; // Saturday from 11h onwards - OPEN
    }
    if (dayOfWeek === 0 && hour < 20) {
      return false; // Sunday before 20h - OPEN
    }
    
    return true; // All other times - CLOSED
  };

  const getClosureTime = () => {
    return "Dimanche 20h00";
  };

  const getOpeningTime = () => {
    return "Samedi 11h00";
  };

  const getCurrentWeekDates = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const monday = new Date(now);
    monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    monday.setHours(0, 0, 0, 0);
    
    const weekDates = {};
    const days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    
    days.forEach((day, index) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + index);
      weekDates[day] = date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short'
      });
    });
    
    return weekDates;
  };

  const getWeekRange = () => {
    // Use getNextMonday() to ensure consistency
    const mondayStr = getNextMonday(); // Returns "YYYY-MM-DD"
    const [year, month, day] = mondayStr.split('-').map(Number);
    const monday = new Date(year, month - 1, day);
    
    // Saturday is 5 days after Monday (Lundi-Samedi)
    const saturday = new Date(year, month - 1, day + 5);
    
    const mondayFormatted = monday.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long'
    });
    
    const saturdayFormatted = saturday.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long'
    });
    
    return `du ${mondayFormatted} au ${saturdayFormatted}`;
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    
    // Check if account is inactive - redirect to activation/renewal page
    if (!parsedUser.is_active) {
      // If they have 0 sessions, it's a renewal case
      if (parsedUser.remaining_sessions === 0) {
        toast.error('Votre pack de séances est terminé. Contactez votre coach pour renouveler.');
      }
      navigate('/activate');
      return;
    }

    fetchUserProfile();
    fetchBookings();
    fetchStats();
    
    // Fetch weekly planning (using admin endpoint for consistency)
    fetchWeeklyPlanning();
    fetchSlotsAvailability(); // Fetch availability for each slot
  }, [navigate]);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erreur de chargement du profil');

      const data = await response.json();
      setUser(data);
      localStorage.setItem('user', JSON.stringify(data));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAllBookings(data.bookings || []);
        
        // Separate upcoming bookings (for current week view)
        const now = new Date();
        const today = now.toISOString().split('T')[0];
        const upcoming = (data.bookings || []).filter(b => b.week_start_date >= today);
        setBookings(upcoming);
      }
    } catch (error) {
      console.error('Erreur de chargement des réservations:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Erreur de chargement des statistiques:', error);
    }
  };

  // NEW: Fetch weekly planning using SAME endpoint as Admin Planning Final
  const fetchWeeklyPlanning = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Calculate Monday of NEXT week (SAME logic as Admin FinalSchedule)
      const weekStart = getNextMonday();
      
      console.log('📅 Loading weekly planning for:', weekStart);
      
      // Use CLIENT endpoint (accessible with client token)
      const response = await fetch(`${API_URL}/api/client/weekly-schedule/${weekStart}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Weekly planning loaded:', data.bookings?.length, 'bookings');
        setWeeklyPlanningBookings(data.bookings || []);
      } else {
        console.error('❌ Failed to load weekly planning');
      }
    } catch (error) {
      console.error('❌ Error:', error);
    }
  };

  // Fetch slots availability (how many spots left per slot)
  const fetchSlotsAvailability = async () => {
    try {
      const token = localStorage.getItem('token');
      const weekStart = getNextMonday();
      
      const response = await fetch(`${API_URL}/api/client/slots-availability/${weekStart}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Convert array to object for easy lookup: {day-time: {available, total, is_full}}
        const availabilityMap = {};
        data.slots?.forEach(slot => {
          const key = `${slot.day}-${slot.time_slot}`;
          availabilityMap[key] = {
            available: slot.available_spots,
            total: slot.max_capacity,
            is_full: slot.is_full
          };
        });
        setSlotsAvailability(availabilityMap);
      }
    } catch (error) {
      console.error('Error fetching slots availability:', error);
    }
  };

  const handleSlotToggle = (day, time) => {
    const slotKey = `${day}-${time}`;
    
    setSelectedSlots(prev => {
      if (prev.includes(slotKey)) {
        // Remove slot
        return prev.filter(s => s !== slotKey);
      } else {
        // Add slot (no error message, just add if under limit)
        return [...prev, slotKey];
      }
    });
  };

  const handleOpenConfirmation = () => {
    if (selectedSlots.length === 0) {
      toast.error('Veuillez sélectionner au moins un créneau');
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    await handleSubmitAvailability();
    setShowConfirmDialog(false);
  };

  const getNextMonday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
    const hour = today.getHours();
    
    let daysUntilMonday;
    
    // PENDANT LA FENÊTRE DE RÉSERVATION (Samedi 11h → Dimanche 20h)
    // On réserve pour LA SEMAINE PROCHAINE
    if ((dayOfWeek === 6 && hour >= 11) || dayOfWeek === 0) {
      // Samedi après 11h OU Dimanche (peu importe l'heure jusqu'à 20h)
      // → Aller au PROCHAIN lundi
      if (dayOfWeek === 6) {
        // Samedi → Lundi suivant = +2 jours
        daysUntilMonday = 2;
      } else {
        // Dimanche → Lundi suivant = +1 jour
        daysUntilMonday = 1;
      }
    } 
    // HORS FENÊTRE DE RÉSERVATION
    // On affiche la semaine actuelle
    else if (dayOfWeek === 1) {
      // Lundi → ce lundi
      daysUntilMonday = 0;
    } else if (dayOfWeek === 0) {
      // Dimanche avant réouverture → semaine actuelle
      daysUntilMonday = 1;
    } else {
      // Mardi-Vendredi ou Samedi avant 11h → Lundi de cette semaine
      daysUntilMonday = -(dayOfWeek - 1);
    }
    
    const monday = new Date(today);
    monday.setDate(today.getDate() + daysUntilMonday);
    return monday.toISOString().split('T')[0];
  };

  // Check if client already has bookings for next week
  const hasBookingsForNextWeek = () => {
    const nextWeekStart = getNextMonday();
    return bookings.some(b => b.week_start_date === nextWeekStart);
  };

  const handleSubmitAvailability = async () => {
    if (selectedSlots.length === 0) {
      toast.error('Veuillez sélectionner au moins un créneau');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const weekStartDate = getNextMonday();

      const availabilities = selectedSlots.map(slot => {
        const [day, time] = slot.split('-');
        return { day, time };
      });

      const response = await fetch(`${API_URL}/api/client/availability`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          week_start_date: weekStartDate,
          availabilities
        })
      });

      if (!response.ok) throw new Error('Erreur lors de la soumission');

      toast.success('Disponibilités envoyées avec succès !');
      setSelectedSlots([]);
      // Reload bookings to update UI
      await fetchBookings();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">
              Bienvenue, {user?.first_name} !
            </h1>
            <p className="text-zinc-400 mt-1">{user?.email}</p>
          </div>
          <Button 
            variant="outline" 
            onClick={handleLogout}
            className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Déconnexion
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
                <Award className="h-4 w-4" />
                Pack de séances
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">
                {user?.remaining_sessions}/{user?.total_sessions}
              </div>
              <p className="text-xs text-zinc-500 mt-1">séances restantes</p>
              {user?.remaining_sessions <= 2 && user?.remaining_sessions > 0 && (
                <div className="mt-2 text-xs text-orange-500 flex items-center gap-1">
                  <span>⚠️</span> Pensez à renouveler bientôt
                </div>
              )}
              {user?.remaining_sessions === 0 && (
                <div className="mt-2 text-xs text-red-500 flex items-center gap-1">
                  <span>⚠️</span> Pack terminé - Contactez le coach
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Séances utilisées
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">
                {user?.used_sessions}
              </div>
              <p className="text-xs text-zinc-500 mt-1">au total</p>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Type de coaching
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-white capitalize">
                {user?.coaching_type === 'private' ? 'Privé' : 'Groupe'}
              </div>
              <p className="text-xs text-zinc-500 mt-1 capitalize">
                {user?.service_category}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Availability Submission */}
        <Card className="bg-zinc-900 border-zinc-800 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Réserver mes séances
            </CardTitle>
            <CardDescription className="text-zinc-400">
              Sélectionnez et réservez vos créneaux pour la semaine prochaine
            </CardDescription>
            <div className="mt-3 bg-blue-950 border border-blue-900 rounded-lg p-3">
              <p className="text-sm text-blue-300">
                <span className="font-semibold">Votre pack : {user?.total_sessions} séances</span>
                <br />
                <span className="text-blue-400">
                  → Vous pouvez réserver jusqu'à <strong>{Math.floor(user?.total_sessions / 4)} créneaux par semaine</strong>
                </span>
                <br />
                <span className="text-blue-400 text-xs mt-1 block">
                  ⚠️ Réservation immédiate : vos séances seront décomptées directement
                </span>
              </p>
            </div>
          </CardHeader>
          <CardContent>
            {/* Already booked for next week */}
            {!isWeekClosed() && hasBookingsForNextWeek() && (
              <div className="mb-6 p-4 bg-green-950 border border-green-900 rounded-lg">
                <p className="text-sm text-green-300">
                  ✅ <strong>Réservations confirmées</strong>
                  <br />
                  Vous avez déjà réservé vos séances pour la semaine prochaine.
                  <br />
                  <span className="text-xs">Vous pourrez réserver à nouveau lors de la prochaine période de réservation (samedi 11h → dimanche 20h)</span>
                </p>
              </div>
            )}
            
            {/* Week closed */}
            {isWeekClosed() && (
              <div className="mb-6 p-4 bg-orange-950 border border-orange-900 rounded-lg">
                <p className="text-sm text-orange-300">
                  ⚠️ <strong>Réservations fermées</strong>
                  <br />
                  Les réservations sont ouvertes uniquement du <strong>samedi 11h00 au dimanche 20h00</strong>
                  <br />
                  <span className="text-xs">Consultez le planning final dans l'onglet "Planning de la semaine"</span>
                </p>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left text-zinc-400 pb-4 pr-4">Horaire</th>
                    {DAYS.map(day => (
                      <th key={day} className="text-center text-zinc-400 pb-4 px-2">
                        <div className="text-xs">{day}</div>
                        {(day === 'Samedi' || day === 'Dimanche') && <div className="text-xs text-zinc-500">(matin)</div>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Generate all time slots, but show conditionally */}
                  {['09:00', '10:30', '12:00', '16:30', '17:30', '18:30', '19:30'].map(time => (
                    <tr key={time} className="border-t border-zinc-800">
                      <td className="py-3 pr-4 text-zinc-300 font-mono text-sm">
                        {time}
                      </td>
                      {DAYS.map(day => {
                        // Check if this time slot is available for this day
                        const daySlots = TIME_SLOTS[day] || [];
                        const isAvailable = daySlots.includes(time);
                        
                        if (!isAvailable) {
                          return (
                            <td key={day} className="text-center py-3 px-2">
                              <div className="text-zinc-700 text-xs">-</div>
                            </td>
                          );
                        }
                        
                        const slotKey = `${day}-${time}`;
                        const isSelected = selectedSlots.includes(slotKey);
                        const maxSlots = Math.floor(user?.total_sessions / 4);
                        
                        // Check slot availability
                        const slotAvail = slotsAvailability[slotKey];
                        const isFull = slotAvail?.is_full || false;
                        
                        // Show "Complet" if slot is full
                        if (isFull) {
                          return (
                            <td key={day} className="text-center py-3 px-2">
                              <div className="text-zinc-500 text-xs font-medium">
                                Complet
                              </div>
                            </td>
                          );
                        }
                        
                        // Disable if max slots reached OR if reservations are closed OR if already booked for next week
                        const isDisabled = (!isSelected && selectedSlots.length >= maxSlots) || isWeekClosed() || hasBookingsForNextWeek();
                        
                        return (
                          <td key={day} className="text-center py-3 px-2">
                            <div className="flex justify-center">
                              <Checkbox
                                checked={isSelected}
                                disabled={isDisabled}
                                onCheckedChange={() => !isWeekClosed() && !hasBookingsForNextWeek() && handleSlotToggle(day, time)}
                                className={`border-zinc-600 ${
                                  isDisabled 
                                    ? 'opacity-30 cursor-not-allowed' 
                                    : 'data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600'
                                }`}
                              />
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex justify-between items-center">
              {!hasBookingsForNextWeek() ? (
                <>
                  <div>
                    <p className="text-sm text-zinc-300 font-semibold">
                      {selectedSlots.length} / {Math.floor(user?.total_sessions / 4)} créneaux sélectionnés
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                      {selectedSlots.length >= Math.floor(user?.total_sessions / 4) 
                        ? '✓ Limite atteinte - Les autres cases sont désactivées' 
                        : `Il vous reste ${Math.floor(user?.total_sessions / 4) - selectedSlots.length} créneau(x) à sélectionner`}
                    </p>
                  </div>
                  <Button
                    onClick={handleOpenConfirmation}
                    disabled={selectedSlots.length === 0}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Réserver mes séances
                  </Button>
                </>
              ) : (
                <div className="w-full text-center">
                  <p className="text-sm text-green-400 font-semibold">
                    ✅ Vos réservations pour la semaine prochaine sont confirmées
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    Consultez le planning dans l'onglet "Planning de la semaine"
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader>
              <DialogTitle>Confirmer vos réservations</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Vous êtes sur le point de réserver {selectedSlots.length} séance(s) pour la semaine prochaine
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-3">
              <div className="bg-orange-950 border border-orange-900 rounded-lg p-3">
                <p className="text-sm text-orange-300">
                  ⚠️ <strong>Attention :</strong> Ces réservations seront confirmées immédiatement et vos séances seront décomptées.
                </p>
              </div>

              <div className="bg-zinc-800 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-zinc-300 mb-3">Créneaux sélectionnés :</h4>
                <div className="grid grid-cols-2 gap-2">
                  {selectedSlots.map((slot, idx) => {
                    const [day, time] = slot.split('-');
                    return (
                      <div key={idx} className="bg-zinc-900 px-3 py-2 rounded text-sm text-zinc-300">
                        {day} - {time}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowConfirmDialog(false)}
                  className="flex-1 border-zinc-700"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleConfirmSubmit}
                  disabled={submitting}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  {submitting ? 'Réservation...' : 'Confirmer la réservation'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bookings Section with Tabs */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <div className="flex gap-4 border-b border-zinc-800 -mb-4">
              <button
                onClick={() => setActiveTab('booking')}
                className={`pb-3 px-2 font-medium transition-colors relative ${
                  activeTab === 'booking'
                    ? 'text-red-600'
                    : 'text-zinc-400 hover:text-zinc-300'
                }`}
              >
                📅 Réserver
                {activeTab === 'booking' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-600"></div>
                )}
              </button>
              {/* Only show Planning tab when reservations are closed (after Sunday 20h) */}
              {isWeekClosed() && (
                <button
                  onClick={() => setActiveTab('schedule')}
                  className={`pb-3 px-2 font-medium transition-colors relative ${
                    activeTab === 'schedule'
                      ? 'text-red-600'
                      : 'text-zinc-400 hover:text-zinc-300'
                  }`}
                >
                  📋 Planning de la semaine
                  {activeTab === 'schedule' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-600"></div>
                  )}
                </button>
              )}
              <button
                onClick={() => setActiveTab('history')}
                className={`pb-3 px-2 font-medium transition-colors relative ${
                  activeTab === 'history'
                    ? 'text-red-600'
                    : 'text-zinc-400 hover:text-zinc-300'
                }`}
              >
                📊 Historique & Stats
                {activeTab === 'history' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-600"></div>
                )}
              </button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {activeTab === 'booking' ? (
              <div>
                <CardTitle className="text-white mb-2">Mes prochaines séances</CardTitle>
                <CardDescription className="text-zinc-400 mb-4">
                  Vos réservations à venir
                </CardDescription>
                {bookings.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">
                    Aucune réservation pour le moment
                  </p>
                ) : (
                  <div className="space-y-3">
                    {bookings.map((booking, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center p-4 bg-zinc-800 rounded-lg border border-zinc-700"
                      >
                        <div>
                          <p className="text-white font-medium">
                            {booking.day} - {booking.time_slot}
                          </p>
                          <p className="text-sm text-zinc-400">
                            Semaine du {booking.week_start_date}
                          </p>
                        </div>
                        <span className="text-xs bg-green-950 text-green-400 px-3 py-1 rounded-full">
                          ✓ Confirmé
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : activeTab === 'schedule' ? (
              <div>
                <CardTitle className="text-white mb-2">
                  Planning de la semaine {getWeekRange()}
                </CardTitle>
                <CardDescription className="text-zinc-400 mb-6">
                  Vos séances confirmées pour cette semaine
                </CardDescription>

                {!isWeekClosed() ? (
                  // Before closure - show waiting message
                  <div className="py-12 text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-zinc-800 rounded-full mb-6">
                      <Clock className="w-10 h-10 text-zinc-500" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-4">
                      Planning en cours de préparation
                    </h3>
                    <p className="text-gray-400 mb-6 max-w-md mx-auto">
                      L'horaire complet de la semaine sera disponible après la clôture des réservations
                    </p>
                    <div className="bg-orange-950 border border-orange-900 rounded-lg p-5 max-w-md mx-auto mb-4">
                      <p className="text-base text-orange-200 font-semibold mb-2">
                        📅 L'horaire sera disponible :
                      </p>
                      <p className="text-orange-300 text-2xl font-bold">
                        Dimanche à 20h00
                      </p>
                      <p className="text-xs text-orange-400 mt-2">
                        Vous pourrez voir avec qui vous vous entraînez
                      </p>
                    </div>
                    <div className="bg-green-950 border border-green-900 rounded-lg p-4 max-w-md mx-auto">
                      <p className="text-sm text-green-300">
                        ✅ <strong>Réservations ouvertes !</strong>
                        <br />
                        <span className="text-green-400">Du samedi 11h00 au dimanche 20h00</span>
                        <br />
                        <span className="text-xs text-green-500 mt-1 block">Vous êtes en pleine période de réservation</span>
                      </p>
                    </div>
                    <p className="text-xs text-zinc-500 mt-6">
                      Allez dans l'onglet "Réserver" pour choisir vos créneaux
                    </p>
                  </div>
                ) : (
                  // After closure - show weekly planning (SAME as Admin Planning Final)
                  <>
                    <div className="mb-4 p-3 bg-green-950 border border-green-900 rounded-lg">
                      <p className="text-sm text-green-300">
                        ✅ <strong>Planning de la semaine finalisé</strong> - Voyez qui s'entraîne avec vous !
                      </p>
                    </div>

                    {/* Yellow table - SAME rendering as Admin FinalSchedule */}
                    <div className="bg-white rounded-lg p-6">
                      <div className="text-center mb-4">
                        <h2 className="text-2xl font-bold text-black mb-1">Horaire de la Semaine</h2>
                        <p className="text-gray-600 text-sm">{getWeekRange()}</p>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-amber-100">
                              <th className="border-2 border-gray-400 p-2 w-20 font-bold text-black text-sm">H / J</th>
                              {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'].map(day => (
                                <th key={day} className="border-2 border-gray-400 p-2 font-bold text-black text-sm">
                                  {day}
                                  {(day === 'Samedi' || day === 'Dimanche') && <div className="text-xs font-normal">(matin)</div>}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {['09:00', '10:30', '12:00', '16:30', '17:30', '18:30', '19:30'].map((time, idx) => {
                              const rows = [];
                              
                              rows.push(
                                <tr key={time} className={idx % 2 === 0 ? 'bg-amber-50' : 'bg-white'}>
                                  <td className="border-2 border-gray-400 p-2 font-bold text-center bg-amber-100 text-black text-sm">
                                    {time}
                                  </td>
                                  {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'].map(day => {
                                    // Check if this time slot is available for this day
                                    const daySlots = TIME_SLOTS[day] || [];
                                    const isAvailable = daySlots.includes(time);
                                    
                                    if (!isAvailable) {
                                      return (
                                        <td key={day} className="border-2 border-gray-400 p-2 align-top bg-gray-300">
                                          <div className="text-gray-500 text-center text-xs">-</div>
                                        </td>
                                      );
                                    }

                                    // Get all clients for this slot
                                    const clients = weeklyPlanningBookings.filter(b => 
                                      b.day === day && b.time_slot === time
                                    );
                                    
                                    // Deduplicate by client email
                                    const uniqueClients = Array.from(
                                      new Map(clients.map(b => [b.client.email, b.client])).values()
                                    );

                                    return (
                                      <td key={day} className="border-2 border-gray-400 p-2 align-top">
                                        {uniqueClients.length > 0 ? (
                                          <div className="text-sm font-medium text-black">
                                            {uniqueClients.map((client, i) => (
                                              <span key={client.email} className={user && client.email === user.email ? 'font-bold text-green-700' : ''}>
                                                {user && client.email === user.email && '✓ '}
                                                {client.first_name}
                                                {i < uniqueClients.length - 1 && <span className="text-gray-500"> / </span>}
                                              </span>
                                            ))}
                                          </div>
                                        ) : (
                                          <div className="text-gray-300 text-center text-sm">-</div>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );

                              // Add pause row after 12:00
                              if (time === '12:00') {
                                rows.push(
                                  <tr key="pause" className="bg-gray-200">
                                    <td colSpan={8} className="border-2 border-gray-400 p-2 text-center font-bold text-black text-sm">
                                      Pause
                                    </td>
                                  </tr>
                                );
                              }

                              return rows;
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Note */}
                      <div className="mt-4 p-3 bg-gray-100 rounded border-2 border-gray-300">
                        <p className="text-xs text-gray-700 mb-2">
                          <strong>Note :</strong> Les noms en <strong className="text-green-700">✓ gras vert</strong> indiquent vos propres créneaux
                        </p>
                        <div className="text-xs text-gray-600 space-y-1 mt-3 border-t border-gray-300 pt-3">
                          <p><strong className="text-gray-700">Samedi :</strong> Matin uniquement (09:00–12:00) – Après-midi fermé</p>
                          <p><strong className="text-gray-700">Dimanche :</strong> Matin uniquement (09:00–12:00) – Après-midi fermé – Période de réservation (11h00–20h00)</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div>
                <CardTitle className="text-white mb-2">Historique & Statistiques</CardTitle>
                <CardDescription className="text-zinc-400 mb-6">
                  Votre progression et séances passées
                </CardDescription>

                {/* Stats Cards */}
                {stats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
                      <div className="text-2xl font-bold text-white mb-1">
                        {stats.sessions_completed}
                      </div>
                      <div className="text-xs text-zinc-400">Séances effectuées</div>
                    </div>
                    <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
                      <div className="text-2xl font-bold text-blue-500 mb-1">
                        {stats.upcoming_sessions}
                      </div>
                      <div className="text-xs text-zinc-400">À venir</div>
                    </div>
                    <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
                      <div className="text-2xl font-bold text-green-500 mb-1">
                        {stats.attendance_rate}%
                      </div>
                      <div className="text-xs text-zinc-400">Taux de présence</div>
                    </div>
                    <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
                      <div className="text-2xl font-bold text-purple-500 mb-1">
                        {stats.codes_received}
                      </div>
                      <div className="text-xs text-zinc-400">Packs reçus</div>
                    </div>
                  </div>
                )}

                {/* History List */}
                <div>
                  <h3 className="text-white font-semibold mb-4">Toutes mes séances</h3>
                  {allBookings.length === 0 ? (
                    <p className="text-zinc-500 text-center py-8">
                      Aucun historique pour le moment
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {allBookings.map((booking, index) => {
                        // Determine if booking is past or future
                        const bookingDate = new Date(booking.week_start_date);
                        const now = new Date();
                        const isPast = bookingDate < now;

                        return (
                          <div
                            key={index}
                            className={`flex justify-between items-center p-3 rounded-lg border ${
                              isPast
                                ? 'bg-zinc-800/50 border-zinc-800'
                                : 'bg-zinc-800 border-zinc-700'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full ${isPast ? 'bg-zinc-600' : 'bg-green-500'}`}></div>
                              <div>
                                <p className={`font-medium ${isPast ? 'text-zinc-400' : 'text-white'}`}>
                                  {booking.day} {booking.time_slot}
                                </p>
                                <p className="text-xs text-zinc-500">
                                  {booking.week_start_date}
                                </p>
                              </div>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              isPast
                                ? 'bg-zinc-900 text-zinc-500'
                                : 'bg-green-950 text-green-400'
                            }`}>
                              {isPast ? 'Effectué' : 'À venir'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
